import React, { createContext, useContext, useState, useEffect } from 'react';

// SkipToContent Component
// Allows keyboard users to skip navigation and jump directly to main content
export function SkipToContent() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:p-4 focus:bg-white focus:text-black focus:outline-none focus:ring-2 focus:ring-blue-500 focus:rounded-md"
    >
      Skip to main content
    </a>
  );
}

// AriaAnnouncer Context
type AnnouncerContextType = {
  announce: (message: string, politeness?: 'assertive' | 'polite') => void;
};

const AriaAnnouncerContext = createContext<AnnouncerContextType | null>(null);

// Provider component for aria announcements
export function AriaAnnouncerProvider({ children }: { children: React.ReactNode }) {
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const [politeMessage, setPoliteMessage] = useState('');

  const announce = (message: string, politeness: 'assertive' | 'polite' = 'polite') => {
    if (politeness === 'assertive') {
      setAssertiveMessage(message);
    } else {
      setPoliteMessage(message);
    }

    // Clear message after screen readers have had time to announce it
    setTimeout(() => {
      if (politeness === 'assertive') {
        setAssertiveMessage('');
      } else {
        setPoliteMessage('');
      }
    }, 3000);
  };

  return (
    <AriaAnnouncerContext.Provider value={{ announce }}>
      {children}
      <div
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        data-testid="assertive-announcer"
      >
        {assertiveMessage}
      </div>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="polite-announcer"
      >
        {politeMessage}
      </div>
    </AriaAnnouncerContext.Provider>
  );
}

// Hook to use the announcer
export function useAnnouncer() {
  const context = useContext(AriaAnnouncerContext);
  if (!context) {
    throw new Error('useAnnouncer must be used within an AriaAnnouncerProvider');
  }
  return context;
}

// FocusTrap Component to prevent focus from leaving modal dialogs
interface FocusTrapProps {
  children: React.ReactNode;
  active?: boolean;
  className?: string;
}

export function FocusTrap({ children, active = true, className = '' }: FocusTrapProps) {
  const trapRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !trapRef.current) return;

    const trapEl = trapRef.current;
    const focusableEls = trapEl.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstFocusableEl = focusableEls[0] as HTMLElement;
    const lastFocusableEl = focusableEls[focusableEls.length - 1] as HTMLElement;

    // Save the previously focused element
    const previouslyFocused = document.activeElement as HTMLElement;

    // Focus the first element in the trap
    if (firstFocusableEl) {
      firstFocusableEl.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      // If shift+tab on first element, wrap to last element
      if (e.shiftKey && document.activeElement === firstFocusableEl) {
        e.preventDefault();
        lastFocusableEl.focus();
      }
      // If tab on last element, wrap to first element
      else if (!e.shiftKey && document.activeElement === lastFocusableEl) {
        e.preventDefault();
        firstFocusableEl.focus();
      }
    };

    trapEl.addEventListener('keydown', handleKeyDown);

    return () => {
      trapEl.removeEventListener('keydown', handleKeyDown);
      // Restore focus to previous element when unmounting
      if (previouslyFocused && 'focus' in previouslyFocused) {
        previouslyFocused.focus();
      }
    };
  }, [active]);

  return (
    <div ref={trapRef} className={className}>
      {children}
    </div>
  );
}

// ReducedMotion Hook to respect user's motion preferences
export function useReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
}